import React, { useEffect, useState, useRef, useCallback } from 'react';
// Sentry disabled — see core/utils/sentry.ts.
// import * as Sentry from '@sentry/react';
import {
  initSentry,
  getAccessToken,
  removeAccessToken,
  saveAccessToken,
  useAppDispatch,
  useAppSelector,
  setAppLoading,
  setLoginState,
  setUser,
  setWallets,
  setCurrentApp,
  setIsSkipOnBoarding,
  setShowOnboarding,
  selectIsLoggedIn,
  selectShowOnboarding,
  registerAuthCallbacks,
  logout,
  selectIsAppLoading,
  getUserProfile,
  checkVisitor,
  authService,
  useSubscriptionExpiredModal,
  useProjectArchiveModal,
  useGracePeriodModal,
  selectTheme,
  useStartups,
} from '@ideoz/core';
import {
  IdeozLoading,
  Toaster,
  SmartPlanExpiredModal,
  ProjectArchiveModal,
  GracePeriodModal,
  showToast,
} from '@ideoz/ui';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ARTIFACT_ROUTE_PATHS, PROTOTYPE_ROUTE_PATHS } from './modules/published/publishedRoutes';
import {
  AppSettings,
  Billing,
  LandingPage,
  MyProfile,
  OAuth2Callback,
  GoogleLoginCallback,
  Onboarding,
  PublishedPrototype,
  PublishedArtifact,
  ReportProblem,
  Register,
  Redirect,
  Login,
  ForgotPassword,
  ResetPassword,
  ScopeConversationPage,
  ScopeConversationResolver,
  ScopeFilesPage,
  StartupListPage,
  StartupDesignSystemPage,
  StartupFilesPage,
  StartupDataPage,
  StartupHomePage,
  StartupIntegrationsPage,
  StartupIntegrationDetailPage,
  StartupArtifactsPage,
  StartupWorkScopesPage,
  StartupProfilePage,
  ScopeArtifactsPage,
} from './pages';
import { useWebPageTracking, useClickTracking } from '@ideoz/core/analytics';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import { queryClient } from './lib/queryClient';
import { GOOGLE_AUTH_CONFIG } from './config/googleAuth';
import { isMultiStartupEnabled } from './config/featureFlags';
import { DirectionProvider } from '@radix-ui/react-direction';
import { useTranslation } from 'react-i18next';
import { isRtlLanguage } from './i18n';
import { useWebNavigation } from './hooks/useWebNavigation';
import { MobileOnlyModal } from '@ideoz/ui';
import { InstallAppButton } from './pwa/InstallAppButton';
import { isMobile } from 'react-device-detect';

// Protected Route Component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const location = useLocation();

  // Wait for token validation to complete before redirecting
  // This handles cases where user navigates with ?token= parameter (e.g., from plugin)
  if (isAppLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  if (!isLoggedIn) {
    // Preserve the URL the user was trying to reach (incl. query string, e.g.
    // `?chat=`) so login can send them back to it instead of the home page.
    const target = location.pathname + location.search;
    const redirect = target && target !== '/' ? `?redirect=${encodeURIComponent(target)}` : '';
    return <Navigate to={`/login${redirect}`} replace />;
  }

  return <>{children}</>;
};

// Root Route - always shows landing page
/**
 * Production redirect for `/startups`. The multi-startup list is hidden in prod,
 * where the app treats the user as having a single startup: send them to that
 * startup's home, or to onboarding to create one when they have none.
 */
const StartupsRedirect = () => {
  const { data: startups = [], isFetching, isFetched } = useStartups();

  // Wait for the first fetch before deciding — avoids flashing onboarding for a
  // user who actually has a startup.
  if (isFetching && !isFetched) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  const first = startups[0];
  return <Navigate to={first ? `/${first.slug}/work-scopes` : '/onboarding'} replace />;
};

/**
 * Slug-agnostic `/integrations` entry. The integration OAuth callback redirects
 * the browser here (the backend's static `FRONTEND_REDIRECT_URL` can't know the
 * startup slug). Each user has one startup, so we refresh the connections cache
 * — a connection may have just changed server-side — and send them to that
 * startup's integrations page.
 */
const IntegrationsRedirect = () => {
  const { data: startups = [], isFetched } = useStartups();
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['user-integrations'] });
  }, [queryClient]);

  if (!isFetched) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  const slug = startups[0]?.slug;
  return <Navigate to={slug ? `/${slug}/integrations` : '/'} replace />;
};

/**
 * Gate for `/onboarding`. Onboarding exists only to create the first startup, so
 * a user who already has one can't reach it — they're redirected to that
 * startup's home. New users (no startup) see the flow.
 *
 * The decision is latched on the first resolved fetch and never revisited for
 * the life of the mount: onboarding's own submit creates a startup and
 * invalidates the list mid-flow, and we must NOT redirect out from under that
 * (it would cut off the "Preparing your startup" screen). So once we've decided
 * to show onboarding, we keep showing it until it navigates away itself.
 */
const OnboardingGuard = ({ children }: { children: React.ReactNode }) => {
  const { data: startups = [], isFetched } = useStartups();
  const decision = useRef<{ done: boolean; redirectTo: string | null }>({
    done: false,
    redirectTo: null,
  });

  if (!decision.current.done && isFetched) {
    decision.current = {
      done: true,
      redirectTo: startups.length > 0 ? `/${startups[0].slug}/work-scopes` : null,
    };
  }

  if (!decision.current.done) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  if (decision.current.redirectTo) {
    return <Navigate to={decision.current.redirectTo} replace />;
  }

  return <>{children}</>;
};

// Onboarding redirect is handled in validateToken function
const RootRoute = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const showOnboarding = useAppSelector(selectShowOnboarding);
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const previousPathnameRef = useRef<string>(location.pathname);

  // Reset showOnboarding if user navigates back to root from /onboarding
  // This prevents showing onboarding when user navigates back to landing page
  // IMPORTANT: This useEffect must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    // If user navigated from /onboarding to /, reset showOnboarding immediately
    if (location.pathname === '/' && previousPathname === '/onboarding' && showOnboarding) {
      console.log('Resetting showOnboarding - user navigated back to root from onboarding');
      dispatch(setShowOnboarding(false));
      // Also silently update the API flag to prevent future redirects
      if (isLoggedIn) {
        authService.updateOnboardingShown().catch((error) => {
          console.error('Failed to update onboarding shown flag on navigation back:', error);
        });
      }
    }
  }, [location.pathname, showOnboarding, dispatch, isLoggedIn]);

  // Show loading while app is initializing
  if (isAppLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <IdeozLoading size="lg" />
      </div>
    );
  }

  console.log('RootRoute render:', { showOnboarding, isLoggedIn, pathname: location.pathname });

  // Logged-in users land on My Startups; everyone else goes to /login.
  // Onboarding redirect is handled in validateToken when isOnBoardingShown === false.
  return <Navigate to={isLoggedIn ? '/startups' : '/login'} replace />;
};

// App Content Component (needs to be inside BrowserRouter to use navigate)
function AppContent() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const showOnboarding = useAppSelector(selectShowOnboarding);
  const planDetails = useAppSelector((state: any) => state.app.planDetails);
  const user = useAppSelector((state: any) => state.app.user);
  const [checkingToken, setCheckingToken] = useState(true);
  const hasInitialized = useRef(false);
  const visitorCheckCalled = useRef(false);
  const callbacksRegistered = useRef(false);
  const onboardingRedirected = useRef(false); // Track if we've already redirected to onboarding

  // Navigation for modals
  const navigation = useWebNavigation();

  // Toast helper for modals
  const toastHelper = useCallback(
    (options: { message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => {
      showToast({
        message: options.message,
        severity: options.severity,
      });
    },
    []
  );

  // Project archive modal state (must be declared before subscription expired modal)
  // Web: Don't provide onUpgrade - hook will use built-in Stripe checkout flow
  const projectArchiveModal = useProjectArchiveModal({
    navigation,
    planDetails,
    showToast: toastHelper,
    // onUpgrade not provided - uses Stripe checkout on web
  });

  // Subscription expired modal state
  // Web: Don't provide onRenew - hook will use built-in Stripe checkout flow
  const subscriptionExpiredModal = useSubscriptionExpiredModal({
    navigation,
    planDetails,
    showToast: toastHelper,
    onShowProjectArchiveModal: projectArchiveModal.openModal,
    // onRenew not provided - uses Stripe checkout on web
  });

  // Grace period modal state
  // Shows for users with 2+ projects who haven't seen the notice yet
  const gracePeriodModal = useGracePeriodModal({
    navigation,
    planDetails,
    user,
    showToast: toastHelper,
    // onViewPlans not provided - uses navigation.toBilling on web
  });

  // Analytics — auto page-view & click tracking
  useWebPageTracking(location.pathname);
  useClickTracking();

  // Sync selected theme to the root <html> element so Tailwind's `dark:`
  // variants activate. Runs on boot (after redux-persist rehydrates) and on
  // every user toggle.
  const theme = useAppSelector(selectTheme);
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [theme]);

  // Initialize Google One Tap (shows "Continue as..." popup)
  // Disable on login/register pages to avoid conflicts with the explicit Google button auth flow
  const isOnAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const isOnRedirectPage =
    location.pathname === '/redirect' || location.pathname === '/back-to-figma';
  useGoogleOneTap({
    clientId: GOOGLE_AUTH_CONFIG.CLIENT_ID,
    disabled: isLoggedIn || checkingToken || isOnAuthPage, // Don't show if already logged in, checking token, or on auth pages
  });

  // Register auth callbacks once
  useEffect(() => {
    if (!callbacksRegistered.current) {
      callbacksRegistered.current = true;

      registerAuthCallbacks(
        // onLogout
        () => {
          dispatch(logout());
          // Also clear the react-query cache (separate in-memory store) so an
          // auth-expiry logout doesn't leak the previous user's server data.
          queryClient.clear();
        },
        // onWalletUpdate
        (wallets) => {
          dispatch(setWallets(wallets));
        },
        // onNavigate
        (route) => {
          navigate(route);
        }
      );

      console.log('✅ Auth callbacks registered');
    }
  }, [dispatch, navigate]);

  // Reset onboardingRedirected flag when user navigates back to root and showOnboarding is false
  // This allows re-checking onboarding if needed (e.g., after logout/login or manual navigation back)
  useEffect(() => {
    if ((location.pathname === '/' || location.pathname === '/landing') && !showOnboarding) {
      // Reset the flag when user is on landing page and not being redirected
      // This allows the onboarding check to happen again on next token validation if needed
      onboardingRedirected.current = false;
      console.log('Reset onboardingRedirected flag - user is on landing page');
    }
  }, [location.pathname, showOnboarding]);

  // Handle plugin redirect navigation
  const handlePluginRedirect = (redirectContext: {
    projectId?: string | null;
    conversationId?: string | null;
    mode?: string | null;
  }) => {
    const { projectId, conversationId, mode = 'prototyper' } = redirectContext;

    // Old-flow routes (/projects/:id, /conversation/:id) are retired. After
    // auth, drop the user onto My Startups regardless of the context — they
    // can pick up from there.
    void projectId;
    void conversationId;
    void mode;
    navigate('/startups');
  };

  // Shared authentication success handler
  const handleAuthSuccess = () => {
    setCheckingToken(false);
    dispatch(setAppLoading(false));

    // Handle plugin redirect after authentication
    const redirectStr = localStorage.getItem('ideoz_plugin_redirect');
    if (redirectStr) {
      try {
        const redirect = JSON.parse(redirectStr);
        handlePluginRedirect(redirect);
        localStorage.removeItem('ideoz_plugin_redirect');
      } catch (error) {
        console.error('Error handling plugin redirect:', error);
        localStorage.removeItem('ideoz_plugin_redirect');
      }
    }
  };

  // Shared authentication error handler
  const handleAuthError = async (error: any) => {
    console.error('Error validating access token:', error);
    setCheckingToken(false);
    dispatch(setAppLoading(false));
    removeAccessToken();
    await handleVisitorCheck();
  };

  // Shared token validation function
  const validateToken = async (token: string) => {
    if (typeof window !== 'undefined') {
      (window as any)['appAccessToken'] = token;
    }
    saveAccessToken(token);

    try {
      // Use getUserProfile thunk which will check metadata.isOnBoardingShown and set showOnboarding accordingly
      const userData = await dispatch(getUserProfile(token)).unwrap();

      // Check if user is not on landing page and isOnBoardingShown is false
      // If so, call API to update the flag
      const pathname = window.location.pathname;
      const isOnLandingPage = pathname === '/' || pathname === '/landing';

      // Check if coming from Figma plugin (check URL params)
      const urlParams = new URLSearchParams(window.location.search);
      const figmaSource = urlParams.get('figma_source');
      const isFromPlugin = !!figmaSource;

      // Check both camelCase and snake_case, and handle string values
      const isOnBoardingShownRaw =
        userData?.metadata?.isOnBoardingShown ?? userData?.metadata?.is_onboarding_shown ?? false;
      const isOnBoardingShown =
        typeof isOnBoardingShownRaw === 'string'
          ? isOnBoardingShownRaw.toLowerCase() === 'true'
          : Boolean(isOnBoardingShownRaw);

      console.log('validateToken - onboarding check:', {
        isOnBoardingShown,
        isOnLandingPage,
        pathname,
        onboardingRedirected: onboardingRedirected.current,
        isFromPlugin,
        figmaSource,
        metadata: userData?.metadata,
      });

      // Skip onboarding redirect if coming from Figma plugin
      // The auth handlers will handle the redirect to /redirect page
      if (!isFromPlugin) {
        // Only redirect to onboarding if:
        // 1. isOnBoardingShown is false
        // 2. User is on landing page
        // 3. We haven't already redirected to onboarding (prevents repeated redirects when navigating back)
        if (isOnBoardingShown === false && isOnLandingPage && !onboardingRedirected.current) {
          // User is on landing page and onboarding not shown yet
          // Explicitly set showOnboarding = true and navigate to /onboarding
          console.log('Setting showOnboarding to true and navigating to /onboarding');
          onboardingRedirected.current = true; // Mark that we've redirected
          dispatch(setShowOnboarding(true));
          navigate('/onboarding');
        } else if (!isOnLandingPage && pathname !== '/onboarding' && isOnBoardingShown === false) {
          // User is on a deep page (not landing, not onboarding) with onboarding
          // not yet completed — mark it shown so we don't keep bouncing them.
          // IMPORTANT: never do this while they are ON `/onboarding` — that would
          // mark onboarding complete just for loading/refreshing the page, and
          // the app would then route them out to `/startups`. Completion is
          // recorded by onboarding's own submit (`updateOnboardingShown`).
          authService.updateOnboardingShown().catch((error) => {
            console.error('Failed to update onboarding shown flag:', error);
          });
        }
      } else {
        console.log('Skipping onboarding redirect - user coming from Figma plugin');
      }

      handleAuthSuccess();
    } catch (error) {
      console.error('Error validating access token:', error);

      // Only logout for actual authentication errors, not network/server errors
      const isAuthError =
        (error as any)?.response?.status === 401 ||
        (error as any)?.response?.status === 403 ||
        (error as any)?.code === 'INVALID_TOKEN' ||
        (error as any)?.message?.includes?.('unauthorized') ||
        (error as any)?.message?.includes?.('invalid token');

      if (isAuthError) {
        // Actual authentication error - logout user
        //await handleAuthError(error);
      } else {
        // Network/server error - keep user logged in but stop loading
        console.warn('Non-auth error during token validation, keeping user logged in');
        setCheckingToken(false);
        dispatch(setAppLoading(false));
        // Optionally show a toast notification about connection issues
      }
    }
  };

  // Handle visitor check (with duplicate prevention)
  const handleVisitorCheck = async () => {
    // Prevent duplicate calls
    if (visitorCheckCalled.current) {
      console.log('🚫 Visitor check already called, skipping duplicate');
      return;
    }

    visitorCheckCalled.current = true;

    try {
      await checkVisitor(dispatch);
      // Note: checkVisitor no longer sets showOnboarding - that's handled separately
    } catch (error) {
      console.error('Error checking visitor:', error);
      visitorCheckCalled.current = false; // Reset on error to allow retry
    }
  };

  // Handle no token scenario
  const handleNoToken = async () => {
    setCheckingToken(false);
    await handleVisitorCheck();
  };

  // Initialize web environment
  const initializeWeb = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    const queryFromUrl = urlParams.get('q');
    const projectIdFromUrl = urlParams.get('projectId');
    const conversationIdFromUrl = urlParams.get('conversationId');
    const modeFromUrl = urlParams.get('mode');
    const sourceFromUrl = urlParams.get('source');
    let accessToken = tokenFromUrl || getAccessToken();

    // Store query parameter if present
    if (queryFromUrl?.trim()) {
      localStorage.setItem('ideoz_pending_query', queryFromUrl.trim());
    }

    // Handle plugin redirect
    const isPluginRedirect =
      sourceFromUrl === 'plugin' && (projectIdFromUrl || conversationIdFromUrl);

    if (isPluginRedirect && !accessToken) {
      // Store redirect context for navigation after authentication
      localStorage.setItem(
        'ideoz_plugin_redirect',
        JSON.stringify({
          projectId: projectIdFromUrl,
          conversationId: conversationIdFromUrl,
          mode: modeFromUrl || 'prototyper',
        })
      );
    }

    // Clean URL parameters (security: remove sensitive data from URL)
    if (tokenFromUrl || queryFromUrl || (isPluginRedirect && !accessToken)) {
      const url = new URL(window.location.href);
      if (tokenFromUrl) url.searchParams.delete('token');
      if (queryFromUrl) url.searchParams.delete('q');
      if (isPluginRedirect && !accessToken) {
        // Remove plugin redirect params (context stored in localStorage)
        url.searchParams.delete('projectId');
        url.searchParams.delete('conversationId');
        url.searchParams.delete('source');
        // Keep mode in URL - pages read it directly if needed
      }
      window.history.replaceState({}, document.title, url.toString());
    }

    const storedOnboarding = localStorage.getItem('ideoz_onboarding_complete');

    if (accessToken) {
      // If already logged in and plugin redirect, navigate immediately
      // (navigation will create clean URL, no need to clean current one)
      if (isPluginRedirect && (projectIdFromUrl || conversationIdFromUrl)) {
        handlePluginRedirect({
          projectId: projectIdFromUrl,
          conversationId: conversationIdFromUrl,
          mode: modeFromUrl || 'prototyper',
        });
      }
      await validateToken(accessToken);
    } else {
      await handleNoToken();
    }

    // Handle onboarding
    if (storedOnboarding === 'true') {
      dispatch(setIsSkipOnBoarding(true));
    }

    // Set current app to 'Web' as default
    dispatch(setCurrentApp('Web'));
  };

  // Main initialization effect
  useEffect(() => {
    // Prevent duplicate initialization in React 18 Strict Mode
    if (hasInitialized.current) {
      return;
    }
    hasInitialized.current = true;

    // Sentry disabled — initSentry is now a no-op (see core/utils/sentry.ts).
    // initSentry();

    dispatch(setAppLoading(true));
    initializeWeb();
  }, []);

  const ispPage = location.pathname === '/p' || /^\/p\//.test(location.pathname);

  return (
    <>
      {isMobile && !ispPage && <MobileOnlyModal />}
      <Toaster position="top-right" expand={false} />
      <InstallAppButton />

      {/* Subscription Expired Modal - Appears over any screen except redirect pages */}
      {!isOnRedirectPage && (
        <SmartPlanExpiredModal
          isOpen={subscriptionExpiredModal.isOpen}
          planName={subscriptionExpiredModal.planName}
          onRenewSubscription={subscriptionExpiredModal.onRenewSubscription}
          onProceedToDowngrade={subscriptionExpiredModal.onProceedToDowngrade}
          isProcessing={subscriptionExpiredModal.isProcessing}
          testId="smart-plan-expired-modal"
        />
      )}

      {/* Project Archive Modal - Appears when user with >1 project downgrades */}
      <ProjectArchiveModal
        isOpen={projectArchiveModal.isOpen}
        projects={projectArchiveModal.projects}
        onUpgradeInstead={projectArchiveModal.onUpgradeInstead}
        onKeepProject={projectArchiveModal.onKeepProject}
        isProcessing={projectArchiveModal.isProcessing}
        testId="project-archive-modal"
      />

      {/* Grace Period Modal - Appears for users with 2+ projects during grace period */}
      <GracePeriodModal
        isOpen={gracePeriodModal.isOpen}
        onClose={gracePeriodModal.onClose}
        onViewPlans={gracePeriodModal.onViewPlans}
        gracePeriodEndTime={gracePeriodModal.gracePeriodEndTime}
        isProcessing={gracePeriodModal.isProcessing}
        testId="grace-period-modal"
      />

      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/redirect" element={<Redirect />} />
        <Route path="/back-to-figma" element={<Redirect />} />
        <Route path="/oauth2-callback" element={<OAuth2Callback />} />
        <Route path="/auth/google/callback" element={<GoogleLoginCallback />} />
        {/* Onboarding is auth-only. When multi-startup is on (localhost/testing)
            it's always reachable so users can create additional startups via
            "Add a New Startup". When off (production, single-startup), OnboardingGuard
            bounces a user who already has a startup back to its home — onboarding
            there exists only to create the first one. */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              {isMultiStartupEnabled() ? (
                <Onboarding />
              ) : (
                <OnboardingGuard>
                  <Onboarding />
                </OnboardingGuard>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/myProfile"
          element={
            <ProtectedRoute>
              <MyProfile />
            </ProtectedRoute>
          }
        /> */}
        {/* Billing / plan management. Re-mounted now that paid tiers are back
            (FREE_MODE_ENABLED off): the credit banners, upgrade CTAs, and
            subscription modals all route here via `navigation.toBilling()`. */}
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app-settings"
          element={
            <ProtectedRoute>
              <AppSettings />
            </ProtectedRoute>
          }
        />
        {/* The multi-startup list is gated behind NODE_ENV
            (on for localhost + testing, off for production). When off, the app
            treats the user as having a single startup: `/startups` redirects to
            their startup home (or to onboarding when they have none) instead of
            showing the list. This also covers the imperative navigations to
            `/startups` (RootRoute, sidebar, etc.) without an infinite redirect
            loop. */}
        <Route
          path="/startups"
          element={
            <ProtectedRoute>
              {isMultiStartupEnabled() ? <StartupListPage /> : <StartupsRedirect />}
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/home"
          element={
            <ProtectedRoute>
              <StartupHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/work-scopes"
          element={
            <ProtectedRoute>
              <StartupWorkScopesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/artifacts"
          element={
            <ProtectedRoute>
              <StartupArtifactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/design-system"
          element={
            <ProtectedRoute>
              <StartupDesignSystemPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/files"
          element={
            <ProtectedRoute>
              <StartupFilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/data"
          element={
            <ProtectedRoute>
              <StartupDataPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/profile"
          element={
            <ProtectedRoute>
              <StartupProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <IntegrationsRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/integrations"
          element={
            <ProtectedRoute>
              <StartupIntegrationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/integrations/:toolId"
          element={
            <ProtectedRoute>
              <StartupIntegrationDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/:scopeId/files"
          element={
            <ProtectedRoute>
              <ScopeFilesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/:scopeId/artifacts"
          element={
            <ProtectedRoute>
              <ScopeArtifactsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/:scopeId"
          element={
            <ProtectedRoute>
              <ScopeConversationResolver />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:startupSlug/:scopeId/:conversationId"
          element={
            <ProtectedRoute>
              <ScopeConversationPage />
            </ProtectedRoute>
          }
        />
        {/* Why two patterns each, and why the explicit one matters: `publishedRoutes.ts`. */}
        {PROTOTYPE_ROUTE_PATHS.map((path) => (
          <Route key={path} path={path} element={<PublishedPrototype />} />
        ))}
        {ARTIFACT_ROUTE_PATHS.map((path) => (
          <Route key={path} path={path} element={<PublishedArtifact />} />
        ))}
        <Route
          path="/report-problem"
          element={
            <ProtectedRoute>
              <ReportProblem />
            </ProtectedRoute>
          }
        />
        {/* Catch all route - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

// Main App Component (wraps AppContent with BrowserRouter)
/**
 * Feeds the active language's direction into Radix's DirectionProvider so all
 * Radix primitives (dropdowns, popovers, tooltips, selects) align + flip
 * correctly in RTL. Re-renders on language change via `useTranslation`.
 */
function DirectionGate({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const dir = isRtlLanguage(i18n.resolvedLanguage || i18n.language) ? 'rtl' : 'ltr';
  return <DirectionProvider dir={dir}>{children}</DirectionProvider>;
}

function App() {
  return (
    <BrowserRouter>
      <DirectionGate>
        <AppContent />
      </DirectionGate>
    </BrowserRouter>
  );
}

// Sentry disabled. The crash-screen UI below is preserved via a plain React
// ErrorBoundary instead of Sentry.withErrorBoundary so no events are sent.
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('App crashed:', error, info);
  }

  resetError = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div
        style={{ padding: '40px', textAlign: 'center', maxWidth: '600px', margin: '100px auto' }}
      >
        <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Something went wrong</h2>
        <p style={{ marginBottom: '24px', color: '#6b7280' }}>
          An unexpected error occurred. Please try refreshing the page.
        </p>
        <button
          onClick={this.resetError}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          Try again
        </button>
        <details style={{ marginTop: '24px', textAlign: 'left' }}>
          <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: '14px' }}>
            Error details
          </summary>
          <pre
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor: '#f3f4f6',
              borderRadius: '4px',
              fontSize: '12px',
              overflow: 'auto',
            }}
          >
            {error.message}
          </pre>
        </details>
      </div>
    );
  }
}

export default function WrappedApp() {
  return (
    <AppErrorBoundary>
      <App />
    </AppErrorBoundary>
  );
}
